import { checkCaptureCompatibility, type CaptureCompatibilityIssue } from './capture-compatibility'
import { detectBrowserFamily, isMobileBrowser, type BrowserFamily } from '@/utils/browser-family'

export interface BrowserCapabilities {
  readonly secureContext: boolean
  readonly topLevel: boolean
  readonly mobile: boolean
  readonly family: BrowserFamily
  readonly capture: CaptureCompatibilityIssue | null
  readonly documentPip: boolean
  readonly popupWindow: boolean
  readonly offscreenCanvas: boolean
  readonly canvas2d: boolean
  readonly createImageBitmap: boolean
  readonly wasmSimd: boolean
  readonly subtleCrypto: boolean
  readonly moduleWorker: boolean
  readonly cacheApi: boolean
  readonly indexedDb: boolean
  readonly localStorage: boolean
}

/** WASM SIMD probe module from the WebAssembly SIMD proposal. */
const WASM_SIMD_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
])

export function probeWasmSimd(api: { validate?: (bytes: BufferSource) => boolean } | undefined = globalThis.WebAssembly): boolean {
  if (typeof api?.validate !== 'function') return false
  try { return api.validate(WASM_SIMD_PROBE) }
  catch { return false }
}

export function probeLocalStorage(storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = globalThis.localStorage): boolean {
  if (!storage) return false
  try {
    const key = 'maple-log:capability-probe'
    storage.setItem(key, '1')
    storage.removeItem(key)
    return true
  } catch { return false }
}

export function probeIndexedDb(api: IDBFactory | undefined = globalThis.indexedDB): boolean {
  return typeof api?.open === 'function'
}

export function probeCanvas2d(): boolean {
  if (typeof OffscreenCanvas === 'function') {
    try {
      if (new OffscreenCanvas(1, 1).getContext('2d')) return true
    } catch { /* try a document canvas */ }
  }
  if (typeof document === 'undefined') return false
  try {
    const canvas = document.createElement('canvas')
    return Boolean(canvas.getContext('2d'))
  } catch { return false }
}

export function probeBrowserCapabilities(): BrowserCapabilities {
  const secureContext = globalThis.isSecureContext === true
  const topLevel = typeof window === 'undefined' || window.top === window
  const mobile = typeof navigator === 'undefined' ? false : isMobileBrowser()
  return {
    secureContext,
    topLevel,
    mobile,
    family: typeof navigator === 'undefined' ? 'unknown' : detectBrowserFamily(),
    capture: checkCaptureCompatibility(),
    documentPip: typeof window !== 'undefined' && typeof window.documentPictureInPicture?.requestWindow === 'function',
    popupWindow: typeof window !== 'undefined' && typeof window.open === 'function',
    offscreenCanvas: typeof OffscreenCanvas === 'function',
    canvas2d: probeCanvas2d(),
    createImageBitmap: typeof createImageBitmap === 'function',
    wasmSimd: probeWasmSimd(),
    subtleCrypto: (() => {
      try { return typeof globalThis.crypto?.subtle?.digest === 'function' }
      catch { return false }
    })(),
    moduleWorker: typeof Worker === 'function',
    cacheApi: typeof globalThis.caches?.open === 'function',
    indexedDb: probeIndexedDb(),
    localStorage: probeLocalStorage(),
  }
}

export function ocrRuntimeBlock(capabilities: BrowserCapabilities = probeBrowserCapabilities()): 'insecure-context' | 'simd' | null {
  if (!capabilities.secureContext) return 'insecure-context'
  if (!capabilities.wasmSimd) return 'simd'
  return null
}
